import { API_URL } from '../config';

interface CronStatusResponse {
  success: boolean;
  data: {
    success: boolean;
    data: {
      jobName: string;
      cronExpression: string;
      isEnabled: boolean;
      isRunning: boolean;
      lastModifiedAt: string;
      lastModifiedBy: string | null;
      timezone: string;
      nextExecution: string;
      currentExecution: {
        startedAt: string;
        duration: number;
      } | null;
      lastExecution: {
        startedAt: string;
        completedAt: string;
        status: string;
        recordsProcessed: number;
        errorMessage: string | null;
      } | null;
    };
    timestamp: string;
  };
  error: null;
  timestamp: string;
}

interface ExecutionHistoryResponse {
  success: boolean;
  data: {
    success: boolean;
    data: {
      history: Array<{
        id: number;
        jobName: string;
        status: string;
        startedAt: string;
        completedAt: string;
        recordsProcessed: number;
        errorMessage: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    };
    timestamp: string;
  };
  error: null;
  timestamp: string;
}

interface ManualSyncResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

interface UpdateScheduleResponse {
  success: boolean;
  message: string;
  cronExpression: string;
  timestamp: string;
}

interface ToggleCronResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

class CronService {
  private getHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  async getCronStatus(): Promise<CronStatusResponse> {
    try {
      const headers = this.getHeaders();
      const response = await fetch(`${API_URL}/api/redmine/cron/status`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cron status:', error);
      throw error;
    }
  }

  async getExecutionHistory(limit: number = 20, offset: number = 0): Promise<ExecutionHistoryResponse> {
    try {
      const headers = this.getHeaders();
      const response = await fetch(
        `${API_URL}/api/redmine/cron/history?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching execution history:', error);
      throw error;
    }
  }

  async triggerManualSync(): Promise<ManualSyncResponse> {
    try {
      const headers = this.getHeaders();
      const response = await fetch(`${API_URL}/api/redmine/cron/sync`, {
        method: 'POST',
        headers,
      });

      const data = await response.json();
      
      if (!response.ok) {
        const error = new Error(data.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      throw error;
    }
  }

  async updateSchedule(cronExpression: string, modifiedBy: string): Promise<UpdateScheduleResponse> {
    try {
      const headers = this.getHeaders();
      const response = await fetch(`${API_URL}/api/redmine/cron/schedule`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          cronExpression,
          modifiedBy,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating cron schedule:', error);
      throw error;
    }
  }

  async toggleCron(enabled: boolean): Promise<ToggleCronResponse> {
    try {
      const headers = this.getHeaders();
      const response = await fetch(`${API_URL}/api/redmine/cron/toggle`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          enabled,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling cron job:', error);
      throw error;
    }
  }
}

export const cronService = new CronService();